import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { idpApi } from '../../../../services/api/idpApi';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { useAlert } from '../../../../contexts/AlertContext';
import { IDP_PRIORITIES } from '../../../../constants/idpPriorities';
import { MEETING_REFERENCE_TYPES } from '../../../../constants/meetingReferenceTypes';
import useIDPStoreRepository from '../../../../store/idpStoreRepository';
import EmployeeSelectStep from './EmployeeSelectStep';
import SelectTrackingMethod from '../../../IndividualDevelopmentPlans/components/IDPWizard/SelectTrackingMethod';
import RelationTypeSelectStep from './RelationTypeSelectStep';

const STEPS = ['Select Individual', 'Select Tracking Method', 'Review & Create'];

/**
 * CreateIDPModal - Modal wizard for creating an IDP from within a meeting
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Array} props.mandatoryAttendees - Array of mandatory attendees with employee info
 * @param {string} props.meetingId - The meeting ID for potential linking
 * @param {string} props.talkingPointId - Optional talking point ID for linking at talking point level
 * @param {Function} props.onSuccess - Callback after IDP is created (receives { goalId, linked })
 */
const CreateIDPModal = ({
  open,
  onClose,
  mandatoryAttendees = [],
  meetingId,
  talkingPointId,
  onSuccess,
}) => {
  const { showAlert } = useAlert();
  const updateGoalInStore = useIDPStoreRepository((state) => state.updateGoal);

  const [activeStep, setActiveStep] = useState(0);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMeasurementType, setSelectedMeasurementType] = useState(null);
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [goalStatuses, setGoalStatuses] = useState([]);
  const [relationTypes, setRelationTypes] = useState([]);
  const [selectedRelationType, setSelectedRelationType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  // Track if IDP has been created (for step 4)
  const [createdGoal, setCreatedGoal] = useState(null);

  // Track if step 0 was auto-skipped (single mandatory attendee)
  const [wasAutoSkipped, setWasAutoSkipped] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedEmployee(null);
      setSelectedMeasurementType(null);
      setSelectedRelationType(null);
      setCreatedGoal(null);

      // Auto-skip employee selection if only one mandatory attendee
      if (mandatoryAttendees.length === 1) {
        const singleAttendee = mandatoryAttendees[0];
        setSelectedEmployee(singleAttendee.employee || singleAttendee);
        setActiveStep(1);
        setWasAutoSkipped(true);
      } else {
        setActiveStep(0);
        setWasAutoSkipped(false);
      }

      fetchInitialData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      const [types, statuses, relTypes] = await Promise.all([
        idpApi.getMeasurementTypes(),
        idpApi.getGoalStatuses(),
        meetingsApi.getMeetingRelationTypes(),
      ]);
      setMeasurementTypes(types || []);
      setGoalStatuses(statuses || []);
      setRelationTypes(relTypes || []);
    } catch (err) {
      showAlert('Failed to load initial data', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
    setActiveStep(1);
  };

  const handleNext = () => {
    if (activeStep === 2) {
      handleCreate();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      // Only go back to step 0 if there are multiple mandatory attendees
      if (mandatoryAttendees.length > 1) {
        setActiveStep(0);
        setSelectedEmployee(null);
      }
    } else if (activeStep === 2) {
      setActiveStep(1);
    }
  };

  const handleCreate = async () => {
    if (!selectedEmployee || !selectedMeasurementType) {
      showAlert('Please select both an employee and a tracking method', 'warning');
      return;
    }

    try {
      setIsCreating(true);

      // Find the "Not Started" status as the default
      const notStartedStatus = goalStatuses.find(s => s.name === 'Not Started');

      const goalData = {
        employeeId: selectedEmployee.id,
        measurementTypeId: selectedMeasurementType.id,
        title: 'New Development Goal',
        description: '',
        priority: IDP_PRIORITIES.LOW,
        currentStatusId: notStartedStatus?.id,
        deadlineAt: null,
        assignedAt: new Date().toISOString(),
        actions: [],
        resources: [],
      };

      const result = await idpApi.saveDevelopmentGoal(goalData);

      if (result && result.id) {
        updateGoalInStore(result);
        setCreatedGoal(result);
        setActiveStep(3); // Move to link confirmation step
      } else {
        showAlert('Failed to create development plan', 'error');
      }
    } catch (err) {
      showAlert(err.message || 'Failed to create development plan', 'error', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleLinkToMeeting = async () => {
    if (!createdGoal || !selectedRelationType) {
      return;
    }

    try {
      setIsLinking(true);

      const referenceData = {
        refType: MEETING_REFERENCE_TYPES.IDP_GOAL,
        refEntity: createdGoal.id,
        meetingRelationTypeId: selectedRelationType.id,
      };

      if (talkingPointId) {
        await meetingsApi.addTalkingPointReference(meetingId, talkingPointId, referenceData);
      } else {
        await meetingsApi.addMeetingReference(meetingId, referenceData);
      }

      onSuccess?.({ goalId: createdGoal.id, linked: true });
      onClose();
    } catch (err) {
      showAlert('Failed to link development plan to meeting', 'error', err);
    } finally {
      setIsLinking(false);
    }
  };

  const handleSkipLinking = () => {
    onSuccess?.({ goalId: createdGoal.id, linked: false });
    onClose();
  };

  const isNextDisabled = () => {
    switch (activeStep) {
      case 0:
        return !selectedEmployee;
      case 1:
        return !selectedMeasurementType;
      case 2:
        return isCreating;
      default:
        return false;
    }
  };

  const renderStep0 = () => (
    <EmployeeSelectStep
      mandatoryAttendees={mandatoryAttendees}
      onSelectEmployee={handleSelectEmployee}
      description="Select an individual to create a development plan for"
      emptyStateDescription="Add mandatory attendees to the meeting to create development plans"
    />
  );

  const renderStep1 = () => (
    <SelectTrackingMethod
      measurementTypes={measurementTypes}
      selectedType={selectedMeasurementType}
      onSelect={setSelectedMeasurementType}
      isLoading={isLoading}
    />
  );

  const renderStep2 = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Review Your Selection
      </Typography>

      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Individual
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {selectedEmployee?.fullName || 'Not selected'}
          </Typography>
          {selectedEmployee?.email && (
            <Typography variant="body2" color="text.secondary">
              {selectedEmployee.email}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Tracking Method
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {selectedMeasurementType?.name || 'Not selected'}
          </Typography>
          {selectedMeasurementType?.description && (
            <Typography variant="body2" color="text.secondary">
              {selectedMeasurementType.description}
            </Typography>
          )}
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary">
        Click "Create Development Plan" to create the goal. You'll be able to add
        more details like title, description, actions, and resources afterward.
      </Typography>
    </Box>
  );

  const renderStep3 = () => (
    <Box sx={{ p: 3 }}>
      {/* Success message */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Development Plan Created Successfully!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          A new development plan has been created for {selectedEmployee?.fullName}.
        </Typography>
      </Box>

      {/* Link question */}
      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8fafc', mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={500} gutterBottom>
          Would you like to link this development plan to the {talkingPointId ? 'talking point' : 'meeting'}?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Linking allows you to track this goal in the context of your meeting discussions.
        </Typography>

        <RelationTypeSelectStep
          relationTypes={relationTypes}
          selectedRelationType={selectedRelationType}
          onSelectRelationType={setSelectedRelationType}
        />
      </Paper>
    </Box>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderStep0();
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '85vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6">Create Development Plan</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Stepper - only show for steps 0-2 */}
      {activeStep < 3 && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label, index) => {
              const isDisabledStep = index === 0 && wasAutoSkipped;
              return (
                <Step
                  key={label}
                  completed={index < activeStep}
                  sx={isDisabledStep ? {
                    '& .MuiStepLabel-iconContainer .Mui-completed': {
                      color: 'grey.400',
                    },
                  } : {}}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      )}

      <DialogContent dividers sx={{ minHeight: 350 }}>
        {renderStepContent()}

        {(isCreating || isLinking) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {activeStep < 3 ? (
          <>
            {/* Show Back button on step 1 (if multiple attendees) and step 2 */}
            {((activeStep === 1 && mandatoryAttendees.length > 1) || activeStep === 2) && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                disabled={isCreating}
              >
                Back
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose} disabled={isCreating}>
              Cancel
            </Button>
            {activeStep > 0 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isNextDisabled()}
              >
                {activeStep === 2 ? (
                  isCreating ? 'Creating...' : 'Create Development Plan'
                ) : (
                  'Next'
                )}
              </Button>
            )}
          </>
        ) : (
          <>
            {/* Step 3 - Link confirmation */}
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={handleSkipLinking}
              disabled={isLinking}
            >
              No, Close
            </Button>
            <Button
              variant="contained"
              onClick={handleLinkToMeeting}
              disabled={!selectedRelationType || isLinking}
            >
              {isLinking ? 'Linking...' : `Link to ${talkingPointId ? 'Talking Point' : 'Meeting'}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateIDPModal;
